import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import PrimeModal from '@/components/primeModal'
import { useTranslation } from 'react-i18next'
import { TreeSelect, Form, Tooltip, Space, Input, App, Spin } from 'antd'
import { ReactComponent as TipIcon } from '@/assets/icons/tip.svg'
import { debounce } from 'lodash'
import { formatTreeSelect, findParentKeys } from './utils'

interface TreeSelectProps {
  onSubmit?: (values: any) => void
  queryDictTree: (search: string) => any
  disabledKey?: string
  type: 'move' | 'merge'
}

const TreeSelectModal = (
  { onSubmit, queryDictTree, disabledKey, type }: TreeSelectProps,
  ref: any
) => {
  const { t } = useTranslation()
  const text = {
    move: {
      title: t('ikg.move'),
      tip: t('ikg.moveTip'),
      label: t('ikg.moveToDir'),
      placeholder: t('ikg.movePlaceholder'),
    },
    merge: {
      title: t('ikg.merge'),
      tip: t('ikg.mergeTip'),
      label: t('ikg.mergeToDir'),
      placeholder: t('ikg.mergePlaceholder'),
    },
  }
  const [form] = Form.useForm()
  const [visible, setVisible] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<any>([])
  const { message } = App.useApp()
  const [treeData, setTreeData] = useState<any[]>([])
  const [searchValue, setSearchValue] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const debouncedSetSearchValue = debounce(setSearchValue, 500)

  const fetchTreeData = async (search?: string) => {
    setLoading(true)
    const result = await queryDictTree(search || '')
    setTreeData(result)
    if (search) {
      setExpandedKeys(findParentKeys(result, 'id'))
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!visible) return
    fetchTreeData(searchValue)
  }, [visible, searchValue])

  const open = (record: any) => {
    form.setFieldsValue(record)
    if (record?.keyPath) {
      const keys = record.keyPath.split('/').map((n) => parseInt(n))
      setExpandedKeys(keys.length > 1 ? keys.slice(0, keys.length - 1) : keys)
    }
    setVisible(true)
  }

  const close = () => {
    form.resetFields()
    setVisible(false)
  }

  useImperativeHandle(ref, () => ({
    open,
    close,
  }))

  const submit = async (values: any) => {
    if (typeof onSubmit === 'function') {
      const res: any = await onSubmit(values)
      if (res?.code === 300 && res?.message) {
        form.setFields([{ name: 'targetDirectoryId', errors: [res.message] }])
        return
      }
      close()
      message.success(t('ikg.success', { action: text[type].title }))
    }
  }

  return (
    <PrimeModal
      title={
        <Space>
          <span>{text[type].title}</span>
          <Tooltip title={text[type].tip} placement="right">
            <TipIcon />
          </Tooltip>
        </Space>
      }
      width={640}
      open={visible}
      onCancel={close}
      onOk={() => form.submit()}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name={'targetDirectoryId'}
          label={text[type].label}
          rules={[
            {
              required: true,
              message: text[type].placeholder,
            },
          ]}
        >
          <TreeSelect
            showSearch
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            placeholder={text[type].placeholder}
            treeData={formatTreeSelect({ tree: treeData, disabledKey })}
            filterTreeNode={false}
            // treeNodeFilterProp="title"
            treeNodeLabelProp="fullPath"
            className="custom-tree-select"
            allowClear
            treeExpandedKeys={expandedKeys}
            onTreeExpand={(keys) => setExpandedKeys(keys)}
            onSearch={debouncedSetSearchValue}
            dropdownRender={(node: any) => {
              return <Spin spinning={loading}>{node}</Spin>
            }}
          />
        </Form.Item>
        <Form.Item hidden name={'directoryId'}>
          <Input />
        </Form.Item>
      </Form>
    </PrimeModal>
  )
}

export default forwardRef(TreeSelectModal)
